/**
 * src/components/Financial/DeclinationAspectsSection.jsx
 * 【完全修正版：nowX定義エラー修正・ダークモード対応・タイムライン分析】
 */

import React, { useState, useMemo, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import {
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
} from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  Settings,
  Activity,
  RefreshCw,
  Check,
  Zap,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const getPlanetsList = (isHeliocentric) => {
  if (isHeliocentric) {
    return [
      "Mercury",
      "Venus",
      "Earth",
      "Mars",
      "Jupiter",
      "Saturn",
      "Uranus",
      "Neptune",
      "Pluto",
    ];
  }
  return [
    "Sun",
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
};
const PLANETS = getPlanetsList(false); // Default list for static references if any
const MAJOR_ASPECT_VALS = [0, 60, 90, 120, 180];
const ASPECT_SYMBOLS = { 0: "☌", 60: "⚹", 90: "□", 120: "△", 180: "☍" };

// =============================================================================
// 1. 内部コンポーネント: DeclinationChart (ダークモード対応版)
// =============================================================================

const DeclinationChart = function (props) {
  const baseDate = props.baseDate;
  const isMobile = props.isMobile;
  const targetPlanets = props.targetPlanets;
  const planetColors = props.planetColors;
  const theme = props.theme;
  const isDark = props.isDark;

  const chartHeight = 480;
  const chartPadding = { top: 80, right: 30, bottom: 60, left: 45 };
  const internalWidth = 1000;
  const isHeliocentric = props.isHeliocentric;
  const planetsList = getPlanetsList(isHeliocentric);

  // 1週間分の赤緯データ生成
  const timelineData = useMemo(
    function () {
      const data = [];
      for (let h = 0; h <= 168; h += 3) {
        const d = new Date(baseDate.getTime() + h * 60 * 60 * 1000);
        const points = {};
        planetsList.forEach(function (p) {
          if (targetPlanets[p] === undefined || targetPlanets[p] === false)
            return;
          try {
            if (isHeliocentric) {
              points[p] = Astro.getHeliocentricLatitude(p, d);
            } else if (Astro.getPlanetDeclination !== undefined) {
              points[p] = Astro.getPlanetDeclination(p, d);
            } else {
              const lon = Astro.getPlanetLongitude(p, d);
              const eps = 23.439;
              const rad = function (degVal) {
                return (degVal * Math.PI) / 180;
              };
              points[p] =
                (Math.asin(Math.sin(rad(eps)) * Math.sin(rad(lon))) * 180) /
                Math.PI;
            }
          } catch (e) {
            points[p] = 0;
          }
        });
        data.push({ date: d, points: points, timeValue: d.getTime() });
      }
      return data;
    },
    [baseDate, targetPlanets, isHeliocentric, planetsList],
  );

  // 黄経アスペクトのピーク検出
  const exactLongAspects = useMemo(
    function () {
      const checkPlanets = planetsList.filter(function (p) {
        return targetPlanets[p] === true;
      });
      const peaks = [];
      const scanStep = 1;

      for (let i = 0; i < checkPlanets.length; i++) {
        for (let j = i + 1; j < checkPlanets.length; j++) {
          const p1 = checkPlanets[i];
          const p2 = checkPlanets[j];
          let prevOrb = null;
          let isDescending = false;

          for (let h = 0; h <= 168; h += scanStep) {
            const t = new Date(baseDate.getTime() + h * 60 * 60 * 1000);
            const lon1 = isHeliocentric
              ? Astro.getHeliocentricLongitude(p1, t)
              : Astro.getPlanetLongitude(p1, t);
            const lon2 = isHeliocentric
              ? Astro.getHeliocentricLongitude(p2, t)
              : Astro.getPlanetLongitude(p2, t);
            const diff = Math.abs(lon1 - lon2) % 360;
            const dist = diff > 180 ? 360 - diff : diff;

            let minOrb = 999;
            let bestAng = 0;
            MAJOR_ASPECT_VALS.forEach(function (ang) {
              const o = Math.abs(dist - ang);
              if (o < minOrb) {
                minOrb = o;
                bestAng = ang;
              }
            });

            if (prevOrb !== null) {
              if (minOrb < prevOrb.orb) {
                isDescending = true;
              } else if (isDescending === true && minOrb > prevOrb.orb) {
                if (prevOrb.orb < 0.25) {
                  peaks.push({
                    p1: p1,
                    p2: p2,
                    symbol: prevOrb.symbol,
                    timeValue: t.getTime() - scanStep * 3600000,
                  });
                }
                isDescending = false;
              }
            }
            prevOrb = { orb: minOrb, symbol: ASPECT_SYMBOLS[bestAng] };
          }
        }
      }
      return peaks;
    },
    [baseDate, targetPlanets, isHeliocentric, planetsList],
  );

  const getX = function (timeValue) {
    const startTime = baseDate.getTime();
    const endTime = startTime + 7 * 24 * 60 * 60 * 1000;
    const width = internalWidth - chartPadding.left - chartPadding.right;
    return (
      chartPadding.left +
      ((timeValue - startTime) / (endTime - startTime)) * width
    );
  };

  const getY = function (val) {
    const range = 64;
    const innerH = chartHeight - chartPadding.top - chartPadding.bottom;
    return chartPadding.top + innerH - ((val + 32) * innerH) / range;
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        borderRadius: "16px",
        border: "1px solid " + theme.border,
        padding: "20px",
        marginBottom: "10px",
        overflowX: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px",
        }}
      >
        <Activity size={18} color="rgb(147, 39, 143)" />
        <span
          style={{ fontSize: "15px", fontWeight: "900", color: theme.text }}
        >
          {isHeliocentric ? "日心黄緯" : "赤緯"}&アスペクト(週間) タイムライン
        </span>
      </div>

      <div style={{ minWidth: "900px" }}>
        <svg
          viewBox={"0 0 " + internalWidth + " " + chartHeight}
          width="100%"
          height={chartHeight}
          style={{ overflow: "visible" }}
        >
          {/* 横軸ガイドライン */}
          {[-23.5, -15, 0, 15, 23.5].map(function (deg) {
            return (
              <g key={deg}>
                <line
                  x1={chartPadding.left}
                  x2={internalWidth - chartPadding.right}
                  y1={getY(deg)}
                  y2={getY(deg)}
                  stroke={deg === 0 ? theme.subText : theme.border}
                  strokeWidth={deg === 0 ? 1.5 : 1}
                  strokeDasharray={Math.abs(deg) === 23.5 ? "5 3" : "0"}
                  opacity={isDark ? 0.3 : 0.6}
                />
                <text
                  x={chartPadding.left - 10}
                  y={getY(deg)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="10"
                  fill={theme.subText}
                  fontWeight="bold"
                >
                  {deg}°
                </text>
              </g>
            );
          })}

          {/* 日付境界線 */}
          {Array.from({ length: 8 }).map(function (_, i) {
            const d = new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000);
            const x = getX(d.getTime());
            return (
              <g key={i}>
                <line
                  x1={x}
                  x2={x}
                  y1={chartPadding.top}
                  y2={chartHeight - chartPadding.bottom}
                  stroke={theme.border}
                  strokeWidth="1.5"
                  opacity={0.5}
                />
                <text
                  x={x}
                  y={chartHeight - chartPadding.bottom + 20}
                  textAnchor="middle"
                  fontSize="11"
                  fill={theme.subText}
                  fontWeight="900"
                >
                  {d.getMonth() + 1}/{d.getDate()}
                </text>
              </g>
            );
          })}

          {/* ピークアスペクト表示 */}
          {exactLongAspects.map(function (asp, i) {
            const x = getX(asp.timeValue);
            const stagger = (i % 3) * 12;
            return (
              <g key={"long-" + i}>
                <line
                  x1={x}
                  x2={x}
                  y1={chartPadding.top - 20}
                  y2={chartHeight - chartPadding.bottom}
                  stroke={isDark ? "#60a5fa" : "#3498db"}
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity="0.4"
                />
                <g
                  transform={
                    "translate(" +
                    x +
                    ", " +
                    (chartPadding.top - 45 - stagger) +
                    ")"
                  }
                >
                  <text
                    textAnchor="middle"
                    fontSize="14"
                    fill={planetColors[asp.p1] || theme.text}
                    fontWeight="900"
                    style={{
                      paintOrder: "stroke",
                      stroke: theme.cardBg,
                      strokeWidth: "3px",
                    }}
                  >
                    {PLANET_SYMBOLS[asp.p1]}
                  </text>
                  <text
                    y="12"
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="900"
                    fill={isDark ? "#60a5fa" : "#3498db"}
                    style={{
                      paintOrder: "stroke",
                      stroke: theme.cardBg,
                      strokeWidth: "3px",
                    }}
                  >
                    {asp.symbol}
                  </text>
                  <text
                    y="26"
                    textAnchor="middle"
                    fontSize="14"
                    fill={planetColors[asp.p2] || theme.text}
                    fontWeight="900"
                    style={{
                      paintOrder: "stroke",
                      stroke: theme.cardBg,
                      strokeWidth: "3px",
                    }}
                  >
                    {PLANET_SYMBOLS[asp.p2]}
                  </text>
                </g>
              </g>
            );
          })}

          {/* 赤緯・黄緯曲線 */}
          {planetsList.map(function (p) {
            if (targetPlanets[p] === undefined || targetPlanets[p] === false)
              return null;
            let pathD = "";
            timelineData.forEach(function (d, idx) {
              const x = getX(d.timeValue);
              const y = getY(d.points[p]);
              if (idx === 0) pathD += "M " + x + " " + y;
              else pathD += " L " + x + " " + y;
            });
            const pColor = planetColors[p] || theme.text;
            return (
              <path
                key={p}
                d={pathD}
                fill="none"
                stroke={pColor}
                strokeWidth={p === "Sun" || p === "Moon" ? 3.5 : 2.2}
                opacity={p === "Moon" ? (isDark ? 0.4 : 0.3) : 0.85}
              />
            );
          })}

          {/* NOWライン (修正：即時実行関数でラップし変数を正しく定義) */}
          {(function () {
            var nowX = getX(new Date().getTime());
            return (
              <g>
                <line
                  x1={nowX}
                  x2={nowX}
                  y1={chartPadding.top}
                  y2={chartHeight - chartPadding.bottom}
                  stroke={isDark ? "#fff" : "#2c2d96"}
                  strokeWidth="3"
                />
                <rect
                  x={nowX - 20}
                  y={chartPadding.top - 15}
                  width="40"
                  height="16"
                  rx="4"
                  fill={isDark ? "#fff" : "#2c2d96"}
                />
                <text
                  x={nowX}
                  y={chartPadding.top - 3}
                  textAnchor="middle"
                  fontSize="10"
                  fill={isDark ? "#000" : "#fff"}
                  fontWeight="900"
                >
                  NOW
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
};

// =============================================================================
// 2. メインコンポーネント: DeclinationAspectsSection
// =============================================================================

export default function DeclinationAspectsSection(props) {
  const initialDate = props.date;
  const planetColors = props.planetColors || DEFAULT_PLANET_COLORS;
  const signColors = props.signColors;

  // ダークモード判定
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const isHeliocentric = props.isHeliocentric === true || props.isHeliocentric === "true";
  const planetsList = getPlanetsList(isHeliocentric);

  const [viewDate, setViewDate] = useState(new Date(initialDate));
  const [isMobile, setIsMobile] = useState(false);
  const [targetPlanets, setTargetPlanets] = useState(
    planetsList.reduce(function (acc, p) {
      acc[p] = true;
      return acc;
    }, {}),
  );

  useEffect(
    function () {
      // isHeliocentric が変わった際に targetPlanets をリセット
      setTargetPlanets(
        planetsList.reduce(function (acc, p) {
          acc[p] = true;
          return acc;
        }, {}),
      );
    },
    [isHeliocentric]
  );

  useEffect(
    function () {
      setViewDate(new Date(initialDate));
      const checkMobile = function () {
        setIsMobile(window.innerWidth < 768);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return function () {
        window.removeEventListener("resize", checkMobile);
      };
    },
    [initialDate],
  );

  const [useStrictOrb, setUseStrictOrb] = useState(false);
  const [majorOnly, setMajorOnly] = useState(false);

  const moveWeek = function (offset) {
    const next = new Date(viewDate);
    next.setDate(next.getDate() + offset * 7);
    setViewDate(next);
  };

  const togglePlanet = function (p) {
    setTargetPlanets(function (prev) {
      const next = Object.assign({}, prev);
      next[p] = !prev[p];
      return next;
    });
  };

  const declinationAspects = useMemo(
    function () {
      try {
        const options = {
          includeMoon: !isHeliocentric, // ヘリオ時は月を除外
          useStrictOrb: useStrictOrb,
          majorOnly: majorOnly,
          orb: 1.0,
          strictOrb: 0.2,
          isHeliocentric: isHeliocentric // astronomy.js 側で対応が必要な可能性あり
        };

        const raw = isHeliocentric
          ? Astro.getAllHeliocentricLatitudeAspects(viewDate, options)
          : Astro.getAllDeclinationAspects(viewDate, options);

        return raw.filter(function (asp) {
          return (
            targetPlanets[asp.planet1] === true &&
            targetPlanets[asp.planet2] === true
          );
        });
      } catch (e) {
        return [];
      }
    },
    [viewDate, targetPlanets, useStrictOrb, majorOnly, isHeliocentric],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <div
        style={{
          padding: "5px 0",
          borderBottom:
            "2px solid " + (isDark ? "rgba(255,255,255,0.1)" : "#2c2d9633"),
          marginBottom: "5px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "900",
            color: theme.text,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Activity size={20} color="rgb(147, 39, 143)" />{" "}
          {isHeliocentric ? "日心黄緯" : "赤緯"}&アスペクト(週間)
        </h3>
      </div>

      {/* ナビゲーションバー */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row", // ★ボタンと日付を2段にする
          alignItems: isMobile ? "flex-end" : "center",
          gap: "12px",
          width: isMobile ? "100%" : "auto",
        }}
      >
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={function () {
              moveWeek(-1);
            }}
            style={{
              padding: "8px 14px",
              background: theme.subBg,
              border: "1px solid " + theme.border,
              color: theme.text,
              borderRadius: "10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              fontWeight: "bold",
            }}
          >
            <ChevronLeft size={18} /> 前週
          </button>
          <button
            onClick={function () {
              setViewDate(new Date());
            }}
            style={{
              padding: "8px 14px",
              background: theme.subBg,
              border: "1px solid " + theme.border,
              color: theme.text,
              borderRadius: "10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              fontWeight: "bold",
            }}
          >
            <RefreshCw size={15} /> 今日
          </button>
          <button
            onClick={function () {
              moveWeek(1);
            }}
            style={{
              padding: "8px 14px",
              background: theme.subBg,
              border: "1px solid " + theme.border,
              color: theme.text,
              borderRadius: "10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              fontWeight: "bold",
            }}
          >
            翌週 <ChevronRight size={18} />
          </button>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{ fontSize: "16px", fontWeight: "900", color: theme.text }}
          >
            {viewDate.toLocaleDateString("ja-JP")} 〜
          </div>
        </div>
      </div>

      {/* 天体選択 */}
      <div
        style={{
          padding: "15px 20px",
          background: theme.cardBg,
          borderRadius: "12px",
          border: "1px solid " + theme.border,
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: "800",
            color: theme.subText,
            marginBottom: "12px",
          }}
        >
          分析対象天体の選択
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {planetsList.map(function (p) {
            const isActive = targetPlanets[p] === true;
            const color = planetColors[p] || theme.text;
            return (
              <button
                key={p}
                onClick={function () {
                  togglePlanet(p);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                  background: isActive
                    ? isDark
                      ? "rgba(255,255,255,0.05)"
                      : color + "15"
                    : theme.subBg,
                  border: "1px solid " + (isActive ? color : theme.border),
                  color: isActive ? color : theme.subText,
                }}
              >
                <span style={{ fontSize: "14px" }}>{PLANET_SYMBOLS[p]}</span>
                <span>{p}</span>
                {isActive && <Check size={12} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* チャート */}
      <DeclinationChart
        baseDate={viewDate}
        isMobile={isMobile}
        targetPlanets={targetPlanets}
        planetColors={planetColors}
        theme={theme}
        isDark={isDark}
        isHeliocentric={isHeliocentric} // 追加
      />

      {/* 検出リスト見出し */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "10px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "800",
            color: isDark ? "#60a5fa" : "#2c2d96",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Zap size={18} /> 検出された{isHeliocentric ? "黄緯" : "赤緯"}アスペクト (Parallel):{" "}
          {declinationAspects.length} 件
        </div>
      </div>

      {/* 検出リスト本体 (f105.jpg対応：名称とOrbの衝突防止) */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {declinationAspects.length === 0 ? (
          <div
            style={{
              padding: "50px",
              textAlign: "center",
              color: theme.subText,
              background: theme.cardBg,
              borderRadius: "16px",
              border: "1px dashed " + theme.border,
            }}
          >
            アスペクトはありません
          </div>
        ) : (
          declinationAspects.map(function (asp, i) {
            const isPara = asp.type === "parallel"; // 小文字判定に合わせる
            const isTight = asp.orb <= 0.1;
            return (
              <div
                key={i}
                style={{
                  background: isTight
                    ? isDark
                      ? "rgba(253, 248, 230, 0.05)"
                      : "#f5f5f3"
                    : theme.cardBg,
                  borderRadius: "12px",
                  padding: isMobile ? "15px" : "15px 20px",
                  border:
                    "1px solid " +
                    (isTight
                      ? isDark
                        ? "rgba(249, 217, 127, 0.3)"
                        : "#f9d97f"
                      : theme.border),
                  borderLeft: "6px solid " + (isPara ? "#9b59b6" : "#e67e22"),
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "10px" : "18px",
                }}
              >
                {/* 左：天体アイコン */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    minWidth: isMobile ? "70px" : "100px",
                  }}
                >
                  <span
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      color: planetColors[asp.planet1] || theme.text,
                    }}
                  >
                    {PLANET_SYMBOLS[asp.planet1]}
                  </span>
                  <span
                    style={{
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "900",
                      color: isPara ? "#9b59b6" : "#e67e22",
                    }}
                  >
                    {asp.symbol}
                  </span>
                  <span
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      color: planetColors[asp.planet2] || theme.text,
                    }}
                  >
                    {PLANET_SYMBOLS[asp.planet2]}
                  </span>
                </div>

                {/* 中・右：詳細情報 (モバイル時に衝突を防ぐため縦並びにする) */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? "2px" : "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: "900",
                        color: theme.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {asp.planet1} & {asp.planet2} (
                      {isPara ? "Parallel" : "Contra-P."})
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "900",
                        color: isDark ? "#9ca3af" : "#64748b",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Orb: {asp.orb.toFixed(3)}°
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.subText,
                      marginTop: "4px",
                    }}
                  >
                    {isHeliocentric ? "Lat" : "Dec"}: {asp.dec1.toFixed(2)}° / {asp.dec2.toFixed(2)}°
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
